import React from 'react';
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link';
import { LinkProps as NextLinkProps } from 'next/link';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import useStyles from './styles';
import {
  createNextLinkWrapper,
  ExternalLinkIcon,
  resolveLinkRouting,
} from '../../electrons/linkRouting';

const Link = React.forwardRef<HTMLAnchorElement, GeneralLinkProps>(
  function Link(props, ref) {
    const { classes } = useStyles();
    const {
      inline = false,
      className,
      children,
      href,
      as,
      external: externalProp,
      showExternalIcon = true,
      underline = 'hover',
      color: colorProp,
      target,
      rel,
      prefetch,
      replace,
      scroll,
      shallow,
      locale,
      innerRef,
      activeClassName,
      ...other
    } = props;

    const router = useRouter();
    const linkRef = innerRef || ref;
    const {
      external,
      target: computedTarget,
      rel: computedRel,
    } = resolveLinkRouting({
      href,
      external: externalProp,
      target,
      rel,
    });
    const muiLinkProps = colorProp !== undefined ? { color: colorProp } : {};
    const rootClassName = clsx(
      classes.linkBase,
      !inline && classes.displayAsBlock,
      inline && classes.displayInline,
      className,
      activeClassName &&
        router.pathname === href && {
          [activeClassName]: true,
        },
    );

    const NextLinkWrapper = React.useMemo(
      () =>
        createNextLinkWrapper({
          href,
          as,
          prefetch,
          replace,
          scroll,
          shallow,
          locale,
        }),
      [href, as, prefetch, replace, scroll, shallow, locale],
    );

    if (external) {
      return (
        <MuiLink
          ref={linkRef}
          href={href}
          underline={underline}
          target={computedTarget}
          rel={computedRel}
          className={rootClassName}
          {...muiLinkProps}
          {...other}
        >
          {children}
          {showExternalIcon && <ExternalLinkIcon />}
        </MuiLink>
      );
    }

    return (
      <MuiLink
        ref={linkRef}
        component={NextLinkWrapper}
        href={href}
        underline={underline}
        target={computedTarget}
        rel={computedRel}
        className={rootClassName}
        {...muiLinkProps}
        {...other}
      >
        {children}
      </MuiLink>
    );
  },
);

export type GeneralLinkProps = {
  children: React.ReactNode;
  href: string;
  inline?: boolean;
  external?: boolean;
  as?: string;
  className?: string;
  showExternalIcon?: boolean;
  /** Applies this class when `router.pathname` matches `href` (admin sidebar nav). */
  activeClassName?: string;
  /** @deprecated Prefer ref. Kept for backward compatibility. */
  innerRef?: React.Ref<HTMLAnchorElement>;
} & Omit<MuiLinkProps, 'href' | 'children' | 'component'> &
  Omit<NextLinkProps, 'href' | 'children'>;

Link.displayName = 'Link';

export default Link;
